import { X } from 'lucide-react';
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import styles from './Dialog.module.css';

export type DialogProps = {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  variant?: 'center' | 'side' | 'popover';
  size?: 'regular' | 'wide';
  subtitle?: ReactNode;
  closeLabel?: string;
};

export const Dialog = ({
  open,
  title,
  onClose,
  children,
  footer,
  variant = 'center',
  size = 'regular',
  subtitle,
  closeLabel = 'Close',
}: DialogProps) => {
  const titleId = useId();
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const element = dialogRef.current;
    if (!element) return;
    if (open && !element.open) element.showModal();
    if (!open && element.open) element.close();
  }, [open]);

  useEffect(() => {
    const element = dialogRef.current;
    if (!element) return;
    const onCancel = (event: Event) => {
      event.preventDefault();
      onClose();
    };
    const onBackdropClick = (event: MouseEvent) => {
      if (event.target === element) onClose();
    };
    element.addEventListener('cancel', onCancel);
    element.addEventListener('click', onBackdropClick);
    return () => {
      element.removeEventListener('cancel', onCancel);
      element.removeEventListener('click', onBackdropClick);
    };
  }, [onClose]);

  if (!open) return null;
  return createPortal(
    <dialog
      ref={dialogRef}
      aria-labelledby={titleId}
      className={[
        styles['dialog'],
        variant === 'center' ? '' : styles[variant],
        size === 'wide' ? styles['wide'] : '',
      ].join(' ')}
    >
      <div className={styles['panel']}>
        <header className={styles['header']}>
          <div className={styles['heading']}>
            <h2 id={titleId} className={styles['title']}>
              {title}
            </h2>
            {subtitle === undefined ? null : <div className={styles['subtitle']}>{subtitle}</div>}
          </div>
          <button type="button" className={styles['close']} onClick={onClose} aria-label={closeLabel}>
            <X size={20} aria-hidden="true" />
          </button>
        </header>
        <div className={styles['body']}>{children}</div>
        {footer ? <footer className={styles['footer']}>{footer}</footer> : null}
      </div>
    </dialog>,
    document.body,
  );
};
